import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaEnvelopeOpenText, FaPlus } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import ThreadList from '../components/messages/ThreadList';
import ThreadView from '../components/messages/ThreadView';
import NewThreadForm from '../components/messages/NewThreadForm';
import * as api from '../lib/api';
import { buttonClass, mutedClass } from '../components/uiStyles';

/** The signed-in user's conversation with the site's admin. */
const MessagesPage = () => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { user, isLoading, refresh } = useAuth();

    const [threads, setThreads] = useState([]);
    const [status, setStatus] = useState('loading');
    const [activeId, setActiveId] = useState(null);
    const [composing, setComposing] = useState(false);

    const loadThreads = useCallback(async () => {
        try {
            const data = await api.fetchThreads();
            setThreads(data.threads);
            setStatus('ready');
            // Keeps the badge in the navigation honest after reading or sending.
            refresh();
            return data.threads;
        } catch {
            setStatus('error');
            return [];
        }
    }, [refresh]);

    useEffect(() => {
        if (user) loadThreads();
    }, [user, loadThreads]);

    const onThreadCreated = (thread) => {
        setComposing(false);
        setActiveId(thread.id);
        loadThreads();
    };

    if (isLoading) {
        return (
            <div className="min-h-screen py-8">
                <div className="section-container">
                    <p className={mutedClass(isDarkMode)}>{t('messages.loading')}</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen py-8">
                <div className="section-container max-w-xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`${isDarkMode ? 'bg-dark-overlay' : 'bg-white'} backdrop-blur-md rounded-2xl p-8 shadow-xl text-center`}
                    >
                        <FaEnvelopeOpenText className={`mx-auto text-4xl mb-4 ${isDarkMode ? 'text-primary' : 'text-green-600'}`} />
                        <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>
                            {t('messages.title')}
                        </h1>
                        <p className={`mb-6 ${mutedClass(isDarkMode)}`}>{t('auth.signInHint')}</p>
                        <div className="flex justify-center">
                            <GoogleSignInButton />
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="section-container">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-6"
                >
                    <Link
                        to="/"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isDarkMode
                            ? 'bg-dark/60 text-primary hover:bg-dark border border-primary/20'
                            : 'bg-white text-green-600 hover:bg-gray-50 border border-gray-200'
                            } transition-all duration-300 shadow-md`}
                    >
                        <FaArrowLeft className="rtl:rotate-180" /> {t('useful.back')}
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`${isDarkMode ? 'bg-dark-overlay' : 'bg-white'} backdrop-blur-md rounded-2xl p-6 md:p-8 shadow-xl`}
                >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <div>
                            <h1 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>
                                {t('messages.title')}
                            </h1>
                            <p className={`text-sm ${mutedClass(isDarkMode)}`}>{t('messages.subtitle')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => { setComposing(true); setActiveId(null); }}
                            className={`${buttonClass(isDarkMode, 'primary')} inline-flex items-center gap-2`}
                        >
                            <FaPlus /> {t('messages.newMessage')}
                        </button>
                    </div>

                    {status === 'error' && (
                        <p className="text-sm text-red-500 mb-4">{t('messages.errors.network')}</p>
                    )}

                    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
                        <div className="max-h-[60vh] overflow-y-auto pr-1">
                            <ThreadList
                                threads={threads}
                                activeId={activeId}
                                onSelect={(id) => { setActiveId(id); setComposing(false); }}
                                unreadKey="userUnread"
                            />
                        </div>

                        <div className={`rounded-xl border min-h-[420px] ${isDarkMode ? 'border-primary/10 bg-dark/30' : 'border-gray-200 bg-gray-50'}`}>
                            {composing || (!activeId && threads.length === 0) ? (
                                <div className="p-4">
                                    <h2 className={`font-semibold mb-3 ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>
                                        {t('messages.newMessage')}
                                    </h2>
                                    <NewThreadForm
                                        onCreated={onThreadCreated}
                                        onCancel={threads.length > 0 ? () => setComposing(false) : undefined}
                                    />
                                </div>
                            ) : activeId ? (
                                <ThreadView
                                    key={activeId}
                                    threadId={activeId}
                                    role="user"
                                    onChanged={loadThreads}
                                />
                            ) : (
                                <p className={`p-6 text-sm ${mutedClass(isDarkMode)}`}>{t('messages.pickThread')}</p>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default MessagesPage;
